/**
 * @graphql agg
 */

import React, { type FC } from 'react';
import gql from 'graphql-tag';

import {
	InTrialFeatureGatesBannerWithData as BaseInTrialFeatureGatesBannerWithData,
	type InTrialFeatureGatesBannerWithDataProps,
} from '@atlassian/in-trial-feature-gates-banner';

import { expValEquals } from '@confluence/feature-experiments';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	InTrialFeatureGatesBannerWithDataQuery as InTrialFeatureGatesBannerWithDataQueryType,
	InTrialFeatureGatesBannerWithDataQueryVariables,
} from './__types__/InTrialFeatureGatesBannerWithDataQuery.ts';

const InTrialFeatureGatesBannerWithDataQuery = gql`
	query InTrialFeatureGatesBannerWithDataQuery($cloudId: ID!) {
		tenantContexts(cloudIds: [$cloudId])
			@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
			entitlementInfo(hamsProductKey: "confluence.ondemand") {
				entitlement(where: { AND: [{ inTrialOrPreDunning: true }] }) {
					subscription {
						trial {
							endTimestamp
						}
						accountDetails {
							invoiceGroup {
								invoiceable
								experienceCapabilities {
									configurePaymentV2 {
										experienceUrl
										isAvailableToUser
									}
									configurePayment {
										experienceUrl
										isAvailableToUser
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export const InTrialFeatureGatesBannerWithData: FC<
	Omit<InTrialFeatureGatesBannerWithDataProps, 'aggClient' | 'inTrialFeatureGatesQueryResult'>
> = (props) => {
	const aggClient = getAGGClient();

	const queryResult = useQueryInterop<
		InTrialFeatureGatesBannerWithDataQueryType,
		InTrialFeatureGatesBannerWithDataQueryVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(InTrialFeatureGatesBannerWithDataQuery, {
		client: aggClient,
		variables: { cloudId: props.tenantId },
		skip: !expValEquals('confluence_relay_migration_no_client_passing', 'isEnabled', true),
	});

	const baseProps: InTrialFeatureGatesBannerWithDataProps = expValEquals(
		'confluence_relay_migration_no_client_passing',
		'isEnabled',
		true,
	)
		? {
				inTrialFeatureGatesQueryResult: queryResult,
				...props,
			}
		: {
				aggClient,
				...props,
			};

	return <BaseInTrialFeatureGatesBannerWithData {...baseProps} />;
};
